/* eslint-disable react-refresh/only-export-components -- registry entries intentionally carry lazy components */
import { type ComponentType, type LazyExoticComponent } from 'react';
import type { AssistantRole } from '../auth/roles';
import { lazyWithRetry } from './lazyWithRetry';

type MyAppCategory = 'Utilities' | 'Productivity' | 'IT Tools' | 'Analytics';

interface AppRegistryEntry {
  id: string;
  title: string;
  description: string;
  icon: string;
  path: string;
  minRole: AssistantRole;
  category: MyAppCategory;
  component: LazyExoticComponent<ComponentType>;
}

export const APP_REGISTRY: AppRegistryEntry[] = [
  {
    id: 'calculator',
    title: 'Calculator & Converter',
    description: 'Everyday math, business and finance calculations, and unit conversions.',
    icon: 'dx-icon-formula',
    path: '/apps/calculator',
    minRole: 'AssistantUser',
    category: 'Utilities',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Utilities/CalculatorHubPage').then((m) => ({
        default: m.CalculatorHubPage
      }))
    )
  },
  {
    id: 'time-date',
    title: 'Time & Date',
    description: 'World clocks, timers, epoch conversion, date math, and timezone conversion.',
    icon: 'dx-icon-clock',
    path: '/apps/time-date',
    minRole: 'AssistantUser',
    category: 'Utilities',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Utilities/TimeDateHubPage').then((m) => ({
        default: m.TimeDateHubPage
      }))
    )
  },
  {
    id: 'generators',
    title: 'Generators',
    description: 'Generate secure identifiers, synthetic data, placeholder content, and QR codes.',
    icon: 'dx-icon-add',
    path: '/apps/generators',
    minRole: 'AssistantAdmin',
    category: 'IT Tools',
    component: lazyWithRetry(() =>
      import('../pages/Apps/ITTools/GeneratorsHubPage').then((m) => ({
        default: m.GeneratorsHubPage
      }))
    )
  },
  {
    id: 'emoji-keyboard',
    title: 'Emoji & Symbols',
    description: 'Search emoji, accessible Unicode symbols, entities, and ASCII art to copy.',
    icon: 'dx-icon-favorites',
    path: '/apps/emoji-keyboard',
    minRole: 'AssistantUser',
    category: 'Utilities',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Utilities/EmojiKeyboardPage').then((m) => ({
        default: m.EmojiKeyboardPage
      }))
    )
  },
  {
    id: 'text-editor',
    title: 'Text Editor',
    description: 'Create a plain text, encrypted, log, or env file in My Storage and open it in Workspace.',
    icon: 'dx-icon-textdocument',
    path: '/apps/text-editor',
    minRole: 'AssistantUser',
    category: 'Productivity',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Productivity/TextEditorAppLaunchPage').then((m) => ({
        default: m.TextAppLaunchPage
      }))
    )
  },
  {
    id: 'code-editor',
    title: 'Code Editor',
    description: 'Create a code or config file in My Storage and open it in Workspace.',
    icon: 'dx-icon-codeblock',
    path: '/apps/code-editor',
    minRole: 'AssistantUser',
    category: 'Productivity',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Productivity/CodeEditorAppLaunchPage').then((m) => ({
        default: m.CodeEditorAppLaunchPage
      }))
    )
  },
  {
    id: 'markdown-editor',
    title: 'Markdown Editor',
    description: 'Create a blank markdown file in My Storage and open it in Workspace.',
    icon: 'dx-icon-docfile',
    path: '/apps/markdown-editor',
    minRole: 'AssistantUser',
    category: 'Productivity',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Productivity/TextEditorAppLaunchPage').then((m) => ({
        default: m.MarkdownAppLaunchPage
      }))
    )
  },
  {
    id: 'drawio-editor',
    title: 'Draw.io Editor',
    description: 'Create a blank diagram in My Storage and open it in Workspace.',
    icon: 'dx-icon-hierarchy',
    path: '/apps/drawio',
    minRole: 'AssistantUser',
    category: 'Productivity',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Productivity/TextEditorAppLaunchPage').then((m) => ({
        default: m.DrawioAppLaunchPage
      }))
    )
  },
  {
    id: 'word-doc',
    title: 'Document Editor',
    description: 'Create a blank document in My Storage and open it in Workspace.',
    icon: 'dx-icon-docxfile',
    path: '/apps/word',
    minRole: 'AssistantUser',
    category: 'Productivity',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Productivity/OfficeAppLaunchPage').then((m) => ({
        default: m.WordAppLaunchPage
      }))
    )
  },
  {
    id: 'excel-workbook',
    title: 'Spreadsheet Editor',
    description: 'Create a blank spreadsheet in My Storage and open it in Workspace.',
    icon: 'dx-icon-xlsxfile',
    path: '/apps/excel',
    minRole: 'AssistantUser',
    category: 'Productivity',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Productivity/OfficeAppLaunchPage').then((m) => ({
        default: m.ExcelAppLaunchPage
      }))
    )
  },
  {
    id: 'pdf-viewer',
    title: 'PDF Viewer',
    description: 'Open a PDF from storage and read, search, or annotate it in Workspace.',
    icon: 'dx-icon-pdffile',
    path: '/apps/pdf-viewer',
    minRole: 'AssistantUser',
    category: 'Productivity',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Productivity/PdfViewerAppLaunchPage').then((m) => ({
        default: m.PdfViewerAppLaunchPage
      }))
    )
  },
  {
    id: 'cursor-usage',
    title: 'Cursor Usage',
    description: 'Import Cursor usage CSV and explore personal rollups.',
    icon: 'dx-icon-chart',
    path: '/apps/cursor-usage',
    minRole: 'AssistantAdmin',
    category: 'Productivity',
    component: lazyWithRetry(() =>
      import('../pages/Apps/CursorUsage/CursorUsagePage').then((m) => ({
        default: m.CursorUsagePage
      }))
    )
  },
  {
    id: 'media-library',
    title: 'Media Library',
    description: 'Browse curated GIFs and memes, and manage your private uploads.',
    icon: 'dx-icon-image',
    path: '/apps/media-library',
    minRole: 'AssistantAdmin',
    category: 'Utilities',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Utilities/MediaLibraryPage').then((m) => ({
        default: m.MediaLibraryPage
      }))
    )
  },
  {
    id: 'encoding',
    title: 'Encoding & Character Tools',
    description: 'Encode data, convert number bases, hash content, and inspect characters.',
    icon: 'dx-icon-key',
    path: '/apps/encoding',
    minRole: 'AssistantAdmin',
    category: 'IT Tools',
    component: lazyWithRetry(() =>
      import('../pages/Apps/ITTools/EncodingHubPage').then((m) => ({ default: m.EncodingHubPage }))
    )
  },
  {
    id: 'data',
    title: 'Data Tools',
    description: 'Format, validate, convert, query, and export structured data.',
    icon: 'dx-icon-codeblock',
    path: '/apps/data',
    minRole: 'AssistantAdmin',
    category: 'IT Tools',
    component: lazyWithRetry(() =>
      import('../pages/Apps/ITTools/DataHubPage').then((m) => ({
        default: m.DataHubPage
      }))
    )
  },
  {
    id: 'text',
    title: 'Text & Pattern Tools',
    description: 'Test regular expressions, compare content, and transform text.',
    icon: 'dx-icon-find',
    path: '/apps/text',
    minRole: 'AssistantAdmin',
    category: 'IT Tools',
    component: lazyWithRetry(() =>
      import('../pages/Apps/ITTools/TextHubPage').then((m) => ({ default: m.TextHubPage }))
    )
  },
  {
    id: 'network',
    title: 'Network Tools',
    description: 'Calculate networks, inspect addresses, and perform approved lookups.',
    icon: 'dx-icon-globe',
    path: '/apps/network',
    minRole: 'AssistantAdmin',
    category: 'IT Tools',
    component: lazyWithRetry(() =>
      import('../pages/Apps/ITTools/NetworkHubPage').then((m) => ({
        default: m.NetworkHubPage
      }))
    )
  },
  {
    id: 'developer',
    title: 'Developer Tools',
    description: 'Inspect schedules, protocols, tokens, certificates, environments, and agents.',
    icon: 'dx-icon-codeblock',
    path: '/apps/developer',
    minRole: 'AssistantAdmin',
    category: 'IT Tools',
    component: lazyWithRetry(() =>
      import('../pages/Apps/ITTools/DeveloperHubPage').then((m) => ({
        default: m.DeveloperHubPage
      }))
    )
  },
  {
    id: 'color',
    title: 'Color & Accessibility',
    description: 'Inspect colors, contrast, palettes, and WCAG accessibility.',
    icon: 'dx-icon-palette',
    path: '/apps/color',
    minRole: 'AssistantAdmin',
    category: 'IT Tools',
    component: lazyWithRetry(() =>
      import('../pages/Apps/ITTools/ColorHubPage').then((m) => ({ default: m.ColorHubPage }))
    )
  },
  {
    id: 'data-sources',
    title: 'Data Sources',
    description: 'Register storage files and database connections, then preview, import, or query them into Analytics.',
    icon: 'dx-icon-datatrending',
    path: '/apps/analytics/data-sources',
    minRole: 'AssistantAdmin',
    category: 'Analytics',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Analytics/DataSourcesPage').then((m) => ({
        default: m.DataSourcesPage
      }))
    )
  },
  {
    id: 'data-workspace',
    title: 'Data Workspace',
    description: 'Model authorized sources with XYFlow and materialize imported datasets.',
    icon: 'dx-icon-hierarchy',
    path: '/apps/analytics/workspace',
    minRole: 'AssistantAdmin',
    category: 'Analytics',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Analytics/DataWorkspacePage').then((m) => ({
        default: m.DataWorkspacePage
      }))
    )
  },
  {
    id: 'dashboard-builder',
    title: 'Dashboard Builder',
    description: 'Author DevExpress dashboards against imported warehouse datasets.',
    icon: 'dx-icon-chart',
    path: '/apps/analytics/dashboards',
    minRole: 'AssistantAdmin',
    category: 'Analytics',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Analytics/DashboardBuilderPage').then((m) => ({
        default: m.DashboardBuilderPage
      }))
    )
  },
  {
    id: 'report-builder',
    title: 'Report Builder',
    description: 'Author DevExpress reports against imported warehouse datasets.',
    icon: 'dx-icon-textdocument',
    path: '/apps/analytics/reports',
    minRole: 'AssistantAdmin',
    category: 'Analytics',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Analytics/ReportBuilderPage').then((m) => ({
        default: m.ReportBuilderPage
      }))
    )
  },
  {
    id: 'analytics-library',
    title: 'Analytics Library',
    description: 'Open published dashboards and reports you are authorized to view.',
    icon: 'dx-icon-folder',
    path: '/apps/analytics/library',
    minRole: 'AssistantUser',
    category: 'Analytics',
    component: lazyWithRetry(() =>
      import('../pages/Apps/Analytics/AnalyticsLibraryPage').then((m) => ({
        default: m.AnalyticsLibraryPage
      }))
    )
  }
];

export const APP_REGISTRY_BY_ID = new Map(APP_REGISTRY.map((entry) => [entry.id, entry]));
